// region Imports

import type {
  ComputedRef,
  MaybeRefOrGetter,
  ModelRef,
  Ref,
  UnwrapRef,
  WatchStopHandle,
  WritableComputedRef,
} from "vue";
import { toRef } from "vue";
// endregion Imports

// region Functions

/**
 * Tests value to assure it is an array of strings
 *
 * @param value - Any value that needs to be tested
 */
export function isStringArray(value: unknown): value is string[] {
  return $ck.assert.isArray(value) && value.every($ck.assert.isString);
}

/**
 * A Factory that creates a reusable function to generate the translation path for zodForms. This utility should primarily
 * be used for writing tests.
 *
 * @param formID - Unique identifier for the form being used.
 * @param [zodForm="zodForm"] - The prefix used for the translation path. This is here as an escape hatch. you probably won't need to use this.
 */
export function TranslationPathFactory<T_Data>(
  formID: string,
  zodForm: string = "zodForm",
): (key: keyof Required<T_Data>) => { labelTextPath: string; placeholderPath: string } {
  /**
   * This will generate 2 translation paths.
   *
   * labelTextPath = `${zodForm}.${formID}.${key}.labelText`
   * placeholderPath = `${zodForm}.${formID}.${key}.placeholder`
   *
   * @param key - the key for the property you need to get a path for
   */
  function translationPaths(key: keyof Required<T_Data>) {
    return {
      labelTextPath: `${zodForm}.${formID}.${<string>key}.labelText`,
      placeholderPath: `${zodForm}.${formID}.${<string>key}.placeholder`,
    };
  }
  return translationPaths;
}

/**
 * Returns an empty error object. To be used to initialize/reset the Errors object for the form
 * @private
 */
function __noErrors<T_ZodValidator extends ZodFormSchema>() {
  return new $ck.z.ZodError<$ck.z.infer<T_ZodValidator>>([]).flatten() as UnwrapRef<
    $ck.z.inferFlattenedErrors<T_ZodValidator>
  >;
}

/**
 *
 * @param formID a unique identifier for your form that is used to look up label/placeholder paths in the translation table
 * @param data The default values for your reactive form. This should match the shape of data for your zod validator
 * @param validator The zod validator that will be used to validate your form
 * @param options Additional options including the ability to override most keys on your reactiveForm items.
 */
export function useZodForm<
  T_ZodValidator extends ZodFormSchema,
  T_Data extends $ck.z.infer<T_ZodValidator> = $ck.z.infer<T_ZodValidator>,
  T_Overrides extends DefaultZodFormItemOptions<T_Data> = DefaultZodFormItemOptions<T_Data>,
>(
  formID: string,
  data: ComputedRef<Partial<T_Data>> | MaybeRefOrGetter<Partial<T_Data>> | ModelRef<T_Data> | Partial<T_Data>,
  validator: T_ZodValidator,
  options?: UseZodFormOptions<T_Overrides>,
): UseZodForm<T_ZodValidator, T_Data> {
  // region Scoped Types
  type T_FlattenedErrors = $ck.z.inferFlattenedErrors<T_ZodValidator>;
  type T_FieldErrors = T_FlattenedErrors["fieldErrors"];
  // endregion Scoped Types

  // region Scoped Values
  const { t } = useI18n();

  /**
   * Make our data into a ref we can use internally. this will standardize the data.
   *
   * @private
   */
  const __data = toRef(() => toValue(data) as Partial<T_Data>);

  /**
   * This is our translation path function.
   * All paths will be looked up at `zodForm.${formID}.${fieldName}.${translationKey}
   *
   * @private
   * @example
   *
   * ```js
   * // create tPaths
   * const tPaths = TranslationPathFactory("FormUser");
   * // get our paths we need
   * const path = tPaths("name");
   * // Test to make sure they match
   * path.labelText === "zodForm.FormUser.name.labelText" // true
   * path.placeholder === "zodForm.FormUser.name.placeholder" // true
   * ```
   */
  const __tPaths = TranslationPathFactory<T_Data>(formID);

  /**
   * This splits out our overrides and custom errorMap function from anything else so we can
   * show a helpful warning if you add a key that isn't supported.
   */
  const { overrides, errorMap, preventReactiveRefresh, validateOnBlur, ...__ } = options ?? {};

  /**
   * This is our errors ref. We can update this as well as modify it to our hearts content.
   *
   * @private
   */
  const __errors: Ref<UnwrapRef<T_FlattenedErrors>> = ref(new $ck.z.ZodError<T_Data>([]).flatten());

  /**
   * Okay, this is really complicated. Basically, we need a private ref here that will act as our way of accessing
   * the reactiveForm BEFORE reactiveForm gets defined. `__reactiveForm.value` and `reactiveForm` will share a reference
   * to the same object, so we should be good to update things this way.
   *
   * @private
   */
  const __reactiveForm = ref<ReactiveZodFormItems<T_Data>>();

  /**
   * If we receive a ZodValidator with any sort of refine/superRefine/ etc chained off of it
   * We need to get to the base validator past the effects that are chained so we can inspect it.
   *
   * @private
   */
  const baseValidator = $ck.utils.unwrapZodObject(validator);

  /**
   * Track the last Validation result.
   */
  const lastValidation = ref<$ck.z.SafeParseReturnType<T_Data, T_Data> | undefined>();

  // endregion Scoped Values

  // region Setup
  if (Object.keys(__).length > 0) {
    devConsole.warn(
      `useZodForm is being passed extra options as the fourth argument that have not been defined. Please make sure you are using the overrides or errorMap properties only!`,
    );
    devConsole.warn(`Additional keys passed in options object: ${Object.keys(__).join(", ")}`);
  }

  /**
   * This is main part. We set up our reactiveForm here.
   */
  __reactiveForm.value = (Object.keys(baseValidator.shape) as AllKeys<T_Data>[]).reduce((acc, key) => {
    /**
     * Get our default translation paths
     */
    const { labelTextPath, placeholderPath } = __tPaths(key);
    const fieldOverride = overrides?.[key] as ZodFormItemOptions<typeof key, T_Data> | undefined;

    /**
     * use the labelText override or fallback to our default
     */
    let labelText = fieldOverride?.labelText;
    if (!$ck.assert.isString(labelText)) {
      labelText = t(labelTextPath);
    }

    /**
     * use the placeholder override or fallback to our default
     */
    let placeholder = fieldOverride?.placeholder;
    if (!$ck.assert.isString(placeholder)) {
      placeholder = t(placeholderPath);
    }
    if (placeholder === placeholderPath) {
      placeholder = "";
    }

    /**
     * create our computed errors array. it will use the value in the shared fieldErrors,
     * and if a shared errors object does not exist, it will make one.
     */
    const errors = computed<string[]>({
      get: () => {
        return (__errors.value?.fieldErrors?.[key] ?? []) as string[];
      },
      set: (value: unknown) => {
        if (!__errors.value) {
          clearReactiveFormErrors();
        }
        if (!__errors.value.fieldErrors) {
          __errors.value.fieldErrors = {} as UnwrapRef<T_FieldErrors>;
        }
        if (isStringArray(value) || $ck.assert.isUndefined(value)) {
          (__errors.value.fieldErrors as Record<AllKeys<T_Data>, string[] | undefined>)[key] = value;
        }
      },
    });

    /**
     * make a fresh, unreferenced version of the original data object.
     * We want to make a fresh copy here for the `assignDefault` function so it does not accidentally
     * mess up the original data object.
     */
    const dataCopy = $ck._.cloneDeep(__data.value);

    /**
     * add out item to our key
     */
    acc[key] = {
      name: fieldOverride?.name ?? key,
      value: $ck._.cloneDeep(dataCopy[key]),
      labelText,
      placeholder,
      default: $ck._.cloneDeep(dataCopy[key]),
      display: fieldOverride?.display ?? ((value: (typeof __data.value)[typeof key]) => `${value}`),
      errors,
      ref: undefined,
      bind: __makeBind(key, fieldOverride, errors),
      dirty: false,
    } as ZodFormItem<T_Data[typeof key]>;

    /**
     * If there is a function as `assignDefault` in the overrides, make sure we run it.
     */
    if (typeof fieldOverride?.assignDefault === "function") {
      const value = fieldOverride.assignDefault!(acc[key].value, dataCopy as T_Data);
      /**
       * We probably want to export a symbol that can be returned to skip assign default step here rather than `undefined`
       * in the event something can be `undefined`
       */
      if (!$ck.assert.isUndefined(value)) {
        acc[key].value = $ck._.cloneDeep(value);
        acc[key].default = $ck._.cloneDeep(value);
      }
    }
    return acc;
  }, {} as ReactiveZodFormItems<T_Data>);

  // endregion Setup

  // region Methods

  /**
   * Create our `.bind` object that can be bound to a ZodInput.
   *
   * @private
   * @param key - The field that is currently being handled
   * @param options - any options provided via `overrides`
   * @param errors - our computed error list. This will be the same as the `reactiveForm[key].errors` computed value so they both reference the same thing.
   */
  function __makeBind<T_Key extends AllKeys<T_Data> = AllKeys<T_Data>>(
    key: T_Key,
    options: ZodFormItemOptions<T_Key, T_Data> | undefined,
    originalErrors: WritableComputedRef<string[]>,
  ): ZodFormsBindProps<T_Data[T_Key]> {
    /**
     * Create our model modifiers. these allow us to add info to `v-model` binding
     * This can be updated based on type.
     */
    let modelModifiers: Record<string, true | undefined> = options?.modelModifiers ?? {};

    const keyShape = baseValidator.shape[key];

    /**
     * Get the base type of validator.
     */
    const keyType = keyShape ? $ck.utils.unwrapZod(keyShape) : undefined;
    /**
     * These will be passed to inputs
     */
    const required = keyShape ? $ck.utils.isZodTypeRequired(keyShape) : false;
    let min, max;
    /**
     * Lets adjust our model modifiers based on the keyType.
     */
    if (!options?.modelModifiers) {
      if (keyType instanceof $ck.z.ZodDate) {
        modelModifiers = $ck.utils.createDateModelModifiers({ "date-obj": true });
      }
      if (keyType instanceof $ck.z.ZodString && keyType.isDate) {
        modelModifiers = $ck.utils.createDateModelModifiers({ "date-only": true });
      }
      if (keyType instanceof $ck.z.ZodString && keyType.isDatetime) {
        modelModifiers = $ck.utils.createDateModelModifiers({ string: true });
      }
      if (keyType instanceof $ck.z.ZodNumber) {
        modelModifiers = { number: true };
      }
    }

    /**
     * set the min/max based on the keyType information
     */
    if (keyType instanceof $ck.z.ZodNumber) {
      if (!$ck.assert.isNull(keyType.minValue)) min = keyType.minValue;
      if (!$ck.assert.isNull(keyType.maxValue)) max = keyType.maxValue;
    }
    if (keyType instanceof $ck.z.ZodString) {
      if (!$ck.assert.isNull(keyType.minLength)) min = keyType.minLength;
      if (!$ck.assert.isNull(keyType.maxLength)) max = keyType.maxLength;
    }
    /**
     * This will give us a reference to the reactiveForm object that we can use to make our own computed values.
     */
    const myKey = computed<ZodFormItem<T_Data[T_Key]>>(() => __reactiveForm.value![key]);

    const errors = computed({
      get: () => {
        if (validateOnBlur && !myKey.value?.dirty) {
          return [];
        }
        return originalErrors.value;
      },
      set: (value) => {
        originalErrors.value = value;
      },
    });
    return {
      errors,
      required,
      name: computed(() => myKey.value.name ?? ""),
      labelText: computed(() => myKey.value.labelText ?? ""),
      placeholder: computed(() => myKey.value.placeholder ?? ""),
      modelValue: computed(() => myKey.value.value),
      min,
      max,
      modelModifiers,
      ref: (el) => {
        if (el && myKey.value) {
          if (el instanceof Element) {
            myKey.value.ref = <HTMLElement>el;
          } else {
            myKey.value.ref = <ZodComponent>el;
          }
        }
      },
      "onUpdate:modelValue": (value) => {
        if (myKey.value) {
          myKey.value.dirty = myKey.value.value !== value;
          errors.value = []; // Clear errors on update
        }
        return options?.updateModelValue instanceof Function
          ? options?.updateModelValue(value)
          : (myKey.value!.value = value);
      },
      onBlur: () => {
        if (myKey.value) {
          myKey.value.dirty = true;
        }
        if (validateOnBlur) {
          validateReactiveFormAsync();
        }
      },
    };
  }

  /**
   * Mark all fields as dirty. This is useful for when you want to validate the form after a user has interacted with it.
   */
  function markAllFieldsAsDirty() {
    const __ = <ReactiveZodFormItems<T_Data>>reactiveForm;
    (Object.keys(reactiveForm) as (keyof ReactiveZodFormItems<T_Data>)[]).forEach((key) => {
      __[key].dirty = true;
    });
  }

  /**
   * Validate the reactive form synchronously.
   * Do not use this with fetch data calls, or any async actions.
   */
  function validateReactiveForm(options?: ZodValidationOptions) {
    const returnedData = getDataFromReactiveForm();
    if (options?.markAllFieldsAsDirty) {
      markAllFieldsAsDirty();
    }
    const check = validator.safeParse(returnedData, { errorMap }) as $ck.z.SafeParseReturnType<T_Data, T_Data>;
    if (!check.success) {
      __errors.value = check.error.flatten() as UnwrapRef<$ck.z.typeToFlattenedError<T_Data>>;
    } else {
      clearReactiveFormErrors();
    }
    lastValidation.value = check;
    return check;
  }

  /**
   * Validate the reactive form asynchronously.
   * Use this if you are needing to test things based on fetched data calls.
   */
  async function validateReactiveFormAsync(options?: ZodValidationOptions) {
    const returnedData = getDataFromReactiveForm();
    if (options?.markAllFieldsAsDirty) {
      markAllFieldsAsDirty();
    }
    const check = (await validator.safeParseAsync(returnedData, { errorMap })) as $ck.z.SafeParseReturnType<
      T_Data,
      T_Data
    >;
    if (!check.success) {
      __errors.value = check.error.flatten() as UnwrapRef<$ck.z.typeToFlattenedError<T_Data>>;
    } else {
      clearReactiveFormErrors();
    }
    lastValidation.value = check;
    return check;
  }

  /**
   * Reset our reactive form to the default data state.
   */
  function resetReactiveForm() {
    const __ = <ReactiveZodFormItems<T_Data>>reactiveForm;
    (Object.keys(reactiveForm) as (keyof ReactiveZodFormItems<T_Data>)[]).forEach((key) => {
      __[key].value = $ck._.cloneDeep(__[key].default);
    });
    clearReactiveFormErrors();
  }

  /**
   * Reset our reactive form to the default data state.
   */
  function refreshFormReactiveData() {
    const __ = <ReactiveZodFormItems<T_Data>>reactiveForm;
    (Object.keys(reactiveForm) as (keyof ReactiveZodFormItems<T_Data>)[]).forEach((key) => {
      __[key].value = $ck._.cloneDeep(__data.value[key])!;
      __[key].default = $ck._.cloneDeep(__data.value[key])!;
      const fieldOverride = overrides?.[key] as ZodFormItemOptions<typeof key, T_Data> | undefined;

      /**
       * If there is a function as `assignDefault` in the overrides, make sure we run it.
       */
      if (typeof fieldOverride?.assignDefault === "function") {
        const value = fieldOverride.assignDefault!(__[key].value, $ck._.cloneDeep(__data.value) as T_Data);
        /**
         * We probably want to export a symbol that can be returned to skip assign default step here rather than `undefined`
         * in the event something can be `undefined`
         */
        if (!$ck.assert.isUndefined(value)) {
          __[key].value = $ck._.cloneDeep(value);
          __[key].default = $ck._.cloneDeep(value);
        }
      }
    });
    return validateReactiveFormAsync();
  }

  /**
   * Clear the current error state in the reactive forms
   */
  function clearReactiveFormErrors() {
    __errors.value = __noErrors<T_ZodValidator>();
  }

  /**
   * Get the current state of the reactiveForm as the data object.
   */
  function getDataFromReactiveForm() {
    return (Object.keys(reactiveForm) as (keyof ReactiveZodFormItems<T_Data>)[]).reduce((acc, key) => {
      acc[key] = (<ReactiveZodFormItems<T_Data>>reactiveForm)[key].value;
      return acc;
    }, {} as T_Data);
  }

  /**
   * Scroll to the input of a key within the reactive form.
   *
   * @param key - the name of the property you want to scroll to
   * @param options - any options you want to pass to `.scrollIntoView` on the given element.
   */
  function scrollToNamed(key: AllKeys<T_Data>, options?: ScrollIntoViewOptions) {
    const currentRef = unref(__reactiveForm.value?.[key].ref);
    devConsole.log("scrollToNamed", currentRef);
    if ($ck.assert.isUndefined(currentRef)) return;
    if (currentRef?.scrollIntoView) {
      currentRef.scrollIntoView(options);
    }
  }

  /**
   * Focus a named input within the reactiveForm.
   *
   * @param key - the reactiveForm property name to scroll to
   * @param options - the options to be passed to `.focus()`
   */
  function focusNamed(key: AllKeys<T_Data>, options?: FocusOptions) {
    const currentRef = unref(__reactiveForm.value?.[key].ref);
    devConsole.log("focusNamed", currentRef);
    if ($ck.assert.isUndefined(currentRef)) return;
    if (currentRef instanceof HTMLElement) {
      return currentRef.focus(options);
    }
    if (currentRef?.focusInput) {
      currentRef.focusInput(options);
    }
  }

  // endregion Methods

  /**
   * Create our external `reactiveForm` object
   */
  const reactiveForm = reactive<ReactiveZodFormItems<T_Data>>(__reactiveForm.value as ReactiveZodFormItems<T_Data>);

  /**
   * Run our initial validation pass.
   */
  if (!validateOnBlur) {
    validateReactiveFormAsync().then((e) => {
      if (import.meta.env.VITE_DEBUG_ZOD) {
        devConsole.log(`${formID} initial validation: `, e);
      }
    });
  }

  /*
   * Revalidate form whenever a value changes
   */
  const debouncedAsyncValidate = $ck._.debounce(validateReactiveFormAsync, 500);
  let stopAutomaticRevalidation: WatchStopHandle = () => {};
  if (options && !options.preventAutomaticRevalidation) {
    stopAutomaticRevalidation = watch(
      () =>
        Object.keys(__reactiveForm.value!).map(
          (key) => __reactiveForm.value![key as keyof ReactiveZodFormItems<T_Data>].value,
        ),
      () => debouncedAsyncValidate(),
    );
  }

  let stopWatchingReactiveData: WatchStopHandle = () => {};
  if (!preventReactiveRefresh) {
    stopWatchingReactiveData = watch(__data, () => {
      refreshFormReactiveData().then((e) => {
        if (import.meta.env.VITE_DEBUG_ZOD) {
          devConsole.log(`${formID} refreshed reactive data validation: `, e);
        }
      });
    });
  }

  return {
    reactiveForm,
    reactiveFormErrors: __errors,
    lastValidation,
    validateReactiveForm,
    validateReactiveFormAsync,
    resetReactiveForm,
    refreshFormReactiveData,
    stopWatchingReactiveData,
    stopAutomaticRevalidation,
    clearReactiveFormErrors,
    getDataFromReactiveForm,
    scrollToNamed,
    focusNamed,
  };
}

// endregion Functions

// region BaseZodFormInputs

/**
 * Base Prop Defaults needed by `useZodForm` that can be applied to inputs/selects.
 * Should be coupled with `BaseZodFormInputProps` type to define props.
 *
 * @example
 *
 * ```html
 * <script setup lang="ts">
 *   interface Props extends BaseZodFormInputProps{};
 *   const props = withDefaults(
 *         defineProps<Props>,
 *         {
 *           ...BaseZodFormInputPropsDefaults,
 *           name: "defaultName"
 *         },
 *       );
 * </script>
 * ```
 */
export const BaseZodFormInputPropsDefaults = {
  labelText: "",
  name: "",
  placeholder: "",
  readonly: false,
  required: true,
  disabled: false,
  modelModifiers: undefined,
  errors: () => [],
};

/**
 * This method should be used by any input/select used in a zod form to make
 * it easy to scroll/focus the input in question. make sure to expose the returned functions.
 *
 * @example
 *
 * ```html
 * <script setup lang="ts">
 *   const input = ref<HTMLInputElement | undefined>();
 *   const {scrollIntoView, focusInput} = useZodExposeMethods(input)
 *   defineExpose({ scrollIntoView, focusInput });
 * </script>
 * <template>
 *   <label> name: <input ref="input" name="name" /></label>
 * </template>
 * ```
 *
 * @param inputRef - a ref that is attached to the element to be focused/scrolled to.
 */
export function useZodExposeMethods(inputRef: Ref<HTMLElement | undefined>) {
  onMounted(() => {
    if (inputRef.value?.autofocus) {
      inputRef.value.focus();
    }
  });

  return {
    focusInput(event?: MouseEvent | FocusOptions) {
      const options = event instanceof MouseEvent ? undefined : event;
      inputRef.value?.focus(options);
    },
    scrollIntoView(arg?: boolean | ScrollIntoViewOptions) {
      inputRef.value?.scrollIntoView(arg);
    },
  };
}

// endregion BaseZodFormInputs
